import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Send,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import SocialLoginButtons from '../components/SocialLoginButtons';

const getPasswordValidationSchema = (t: any) => yup.object({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  password: yup.string().required(t('auth.passwordRequired')),
});

const getMagicLinkValidationSchema = () => yup.object({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, requestMagicLink, verifyMagicLink } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Check for magic link token on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkVerification(token);
    }
  }, [searchParams]);
  
  const handleMagicLinkVerification = async (token: string) => {
    try {
      setError(null);
      await verifyMagicLink(token);
    } catch (error: any) {
      setError('Invalid or expired magic link. Please try again.');
    }
  };

  // Password login form
  const passwordFormik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: getPasswordValidationSchema(t),
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      setSuccess(null);
      try {
        await login(values.email, values.password);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Login failed');
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Magic link form
  const magicLinkFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: getMagicLinkValidationSchema(),
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      setSuccess(null);
      try {
        await requestMagicLink(values.email);
        setSuccess('Magic link sent! Check your email and click the link to sign in.');
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to send magic link');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '200%',
            height: '200%',
            top: '-50%',
            left: '-50%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
            animation: 'pulse 15s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '200%',
            height: '200%',
            top: '-50%',
            right: '-50%',
            background: 'radial-gradient(circle, rgba(118, 75, 162, 0.1) 0%, transparent 70%)',
            animation: 'pulse 20s ease-in-out infinite reverse',
          },
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: '400px', padding: '0 16px' }}
      >
        <Card
          sx={{
            width: '100%',
            p: { xs: 3, sm: 4 },
            background: 'rgba(26, 26, 46, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <Typography
                variant="h3"
                className="gradient-text"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                {t('common.appName')}
              </Typography>
            </motion.div>
            <Typography variant="body2" color="text.secondary">
              {t('auth.tagline')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Social Login Buttons */}
          <SocialLoginButtons variant="login" />
          
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              or continue with
            </Typography>
          </Divider>

          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ mb: 3 }}
            variant="fullWidth"
          >
            <Tab label="Password" />
            <Tab label="Magic Link" />
          </Tabs>

          {tabValue === 0 && (
            <form onSubmit={passwordFormik.handleSubmit}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                type="email"
                value={passwordFormik.values.email}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.email && Boolean(passwordFormik.errors.email)}
                helperText={passwordFormik.touched.email && passwordFormik.errors.email}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                id="password"
                name="password"
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                value={passwordFormik.values.password}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.password && Boolean(passwordFormik.errors.password)}
                helperText={passwordFormik.touched.password && passwordFormik.errors.password}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={passwordFormik.isSubmitting}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7788ff 0%, #8659b8 100%)',
                  },
                  mb: 2
                }}
              >
                {passwordFormik.isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('auth.loginButton')
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link 
                  to="/reset-password" 
                  style={{ color: '#8b9eff', textDecoration: 'none', fontSize: '14px' }}
                >
                  Forgot your password?
                </Link>
              </Box>
            </form>
          )}

          {tabValue === 1 && (
            <form onSubmit={magicLinkFormik.handleSubmit}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                type="email"
                value={magicLinkFormik.values.email}
                onChange={magicLinkFormik.handleChange}
                error={magicLinkFormik.touched.email && Boolean(magicLinkFormik.errors.email)}
                helperText={magicLinkFormik.touched.email && magicLinkFormik.errors.email}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={magicLinkFormik.isSubmitting}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
                  },
                }}
                startIcon={magicLinkFormik.isSubmitting ? undefined : <Send />}
              >
                {magicLinkFormik.isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Magic Link'
                )}
              </Button>

              <Alert severity="info" sx={{ mt: 2, fontSize: '12px' }}>
                We'll send you a secure link to sign in without a password.
              </Alert>
            </form>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              New to AutoReelr?{' '}
              <Link to="/signup" style={{ color: '#8b9eff', textDecoration: 'none' }}>
                Create an account
              </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Version 1.0.0 • © 2024 AutoReelr
            </Typography>
          </Box>
        </Card>
      </motion.div>
    </Box>
  );
};

export default Login;